import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageCircle, HandHeart, CornerUpRight, ArrowLeft, Loader2, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  reaction:      { icon: Heart,          color: '#E76F51' },
  comment:       { icon: MessageCircle,  color: '#5B8DEF' },
  reply:         { icon: CornerUpRight,  color: '#5B8DEF' },
  encouragement: { icon: HandHeart,      color: '#D6AD60' },
  journey_nudge: { icon: HandHeart,      color: '#7a9b8e' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const NotificationsPage = () => {
  const { user, isLoading: loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const r = await fetch(`${API_URL}/api/notifications?limit=50`, { headers: authHeaders() });
      if (r.ok) {
        const b = await r.json();
        setItems(b.notifications || []);
      }
    } catch {
      toast.error('Could not load notifications');
    } finally {
      setFetching(false);
    }
  }, [authHeaders]);

  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: authHeaders(),
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* silent */ }
  }, [authHeaders]);

  useEffect(() => {
    if (user) {
      load().then(() => {
        // Marking-read happens immediately after list loads so the badge
        // clears the moment the user visits this page.
        markAllRead();
      });
    }
  }, [user, load, markAllRead]);

  if (loading) return null;
  if (!user)   return <Navigate to="/auth" replace />;

  const openItem = (n) => {
    // Deep-link and mark-read defensively (bulk mark already ran).
    if (n.url) navigate(n.url);
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(180deg, #1c3a33 0%, #0f1f1c 100%)' }} data-testid="notifications-page">
      <div className="px-5 pt-4 pb-2">
        <Link
          to="/"
          data-testid="notifications-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
               style={{ background: 'linear-gradient(135deg, #D6AD60 0%, #AA8347 100%)' }}>
            <Bell className="w-6 h-6 text-[#242824]" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: '#D6AD60' }}>
              Notifications
            </p>
            <h1 className="text-2xl text-[#F8F6F0]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              What&rsquo;s new
            </h1>
          </div>
        </div>
      </div>

      <div className="px-5 mt-4">
        {fetching ? (
          <div className="text-center py-10 text-white/60 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 rounded-2xl bg-white/[0.03] border border-white/10" data-testid="notifications-empty">
            <CheckCheck className="w-8 h-8 text-white/40 mx-auto mb-3" />
            <p className="text-[13px] text-white/70">You&rsquo;re all caught up.</p>
            <p className="text-[11px] text-white/50 mt-1">
              Reactions, comments, and encouragement will show up here.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => {
              const meta = KIND_META[n.kind] || KIND_META.comment;
              const Icon = meta.icon;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => openItem(n)}
                    data-testid={`notification-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-2xl transition-all active:scale-[0.99] ${
                      n.read
                        ? 'bg-white/[0.04] border border-white/10'
                        : 'bg-[#FFFCF3] border border-[#D6AD60]/40'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                         style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}55` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-semibold ${n.read ? 'text-white/90' : 'text-stone-800'}`}>
                        {n.title}
                      </p>
                      <p className={`text-[12px] mt-0.5 whitespace-pre-line break-words ${n.read ? 'text-white/60' : 'text-stone-600'}`}>
                        {n.body}
                      </p>
                      <p className={`text-[10px] mt-1 ${n.read ? 'text-white/40' : 'text-stone-400'}`}>
                        {timeAgo(n.createdAt)}
                      </p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#E76F51' }} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default NotificationsPage;
